import React, { useEffect, useRef, useState } from "react";
import TextArea from "./TextArea";
import "./JsonTextArea.scss";

const isValidType = (parsed, type) =>
  type === "object" ? parsed !== null && typeof parsed === "object" && !Array.isArray(parsed) : Array.isArray(parsed);

const formatJson = (value, type) => {
  if (typeof value === "string") return value;
  if (isValidType(value, type)) return JSON.stringify(value, null, 2);
  return type === "object" ? "{}" : "[]";
};

const LITERAL = /^(-?\d+(\.\d+)?([eE][+-]?\d+)?|true|false|null)$/;

// Loosely scans the text and inserts missing commas, quotes bare keys/values,
// closes unterminated strings and removes trailing commas.
const looseRepair = (raw: string) => {
  let out = "";
  const stack: string[] = [];
  let prev: "start" | "open" | "comma" | "colon" | "value" = "start";
  let i = 0;

  const separate = () => {
    if (prev === "value") out += ",";
  };

  while (i < raw.length) {
    const ch = raw[i];

    if (/\s/.test(ch)) {
      out += ch;
      i++;
    } else if (ch === ",") {
      if (prev === "value") {
        out += ",";
        prev = "comma";
      }
      i++;
    } else if (ch === "{" || ch === "[") {
      separate();
      out += ch;
      stack.push(ch);
      prev = "open";
      i++;
    } else if (ch === "}" || ch === "]") {
      if (prev === "comma") out = out.replace(/,(\s*)$/, "$1");
      if (prev === "colon") out += "null";
      out += ch;
      stack.pop();
      prev = "value";
      i++;
    } else if (ch === ":") {
      out += ch;
      prev = "colon";
      i++;
    } else if (ch === '"') {
      separate();
      let j = i + 1;
      while (j < raw.length && raw[j] !== '"' && raw[j] !== "\n") j += raw[j] === "\\" ? 2 : 1;
      out += raw.slice(i, j) + '"';
      prev = "value";
      i = raw[j] === '"' ? j + 1 : j;
    } else {
      const inObject = stack[stack.length - 1] === "{";
      const expectKey = inObject && prev !== "colon";
      separate();
      let j = i;
      while (j < raw.length && raw[j] !== "\n" && !(expectKey ? ":" === raw[j] : ",]}".includes(raw[j]))) j++;
      const token = raw.slice(i, j).trim();
      out += expectKey || !LITERAL.test(token) ? JSON.stringify(token) : token;
      prev = "value";
      i = j;
    }
  }

  while (stack.length) {
    if (prev === "comma") out = out.replace(/,(\s*)$/, "$1");
    if (prev === "colon") out += "null";
    out += stack.pop() === "{" ? "}" : "]";
    prev = "value";
  }
  return out;
};

const repairJson = (raw, emptyValue, type) => {
  if (raw.trim() === "") return emptyValue;
  try {
    const parsed = JSON.parse(looseRepair(raw));
    if (isValidType(parsed, type)) return JSON.stringify(parsed, null, 2);
  } catch {
    /* invalid */
  }
  return null;
};

const JsonTextArea = ({
  label,
  value,
  onChange,
  type = "array",
  emptyValue = type === "object" ? "{}" : "[]",
  rows = undefined,
  ariaLabel = undefined,
}: {
  label?: string;
  value: any;
  onChange: (value: string) => void;
  type?: "array" | "object";
  emptyValue?: string;
  rows?: number;
  ariaLabel?: string;
}) => {
  const errorMessage = `The field contains an incorrect ${type}.`;
  const [input, setInput] = useState(() => formatJson(value, type));
  const [error, setError] = useState<string | null>(null);
  const lastEmitted = useRef<string | null>(null);

  const emit = (repaired: string) => {
    lastEmitted.current = repaired;
    onChange(repaired);
  };

  useEffect(() => {
    if (value === lastEmitted.current) return;
    const raw = formatJson(value, type);
    const repaired = repairJson(raw, emptyValue, type);
    if (repaired !== null) {
      setInput(repaired);
      setError(null);
      if (repaired !== raw) emit(repaired);
    } else {
      setInput(raw);
      setError(errorMessage);
    }
  }, [value]);

  // While typing: keep the raw text in the textarea so the user can freely
  // type commas, new lines, new items, etc. Only silently pass the repaired
  // value to the parent (so Save always gets valid JSON).
  const handleChange = (e) => {
    const raw = e.target.value;
    setInput(raw);
    const repaired = repairJson(raw, emptyValue, type);
    if (repaired !== null) {
      setError(null);
      emit(repaired);
    } else {
      setError(errorMessage);
    }
  };

  // On blur: reformat the textarea to show the repaired, pretty-printed JSON.
  const handleBlur = () => {
    const repaired = repairJson(input, emptyValue, type);
    if (repaired !== null) {
      setInput(repaired);
      setError(null);
      if (repaired !== input) emit(repaired);
    } else {
      setError(errorMessage);
    }
  };

  return (
    <div className="json-textarea">
      <TextArea
        className="textarea-code-editor"
        label={label}
        rows={rows}
        aria-label={ariaLabel}
        value={input}
        onChange={handleChange}
        onBlur={handleBlur}
      />
      {error && <div className="error-message">{error}</div>}
    </div>
  );
};

export default JsonTextArea;
